/**
 * Montos y tasas escritos a mano en el módulo ERP (2026-09-29, decisión Erwin
 * tras la gratificación "219"): un set de parámetros se guardó con sueldo
 * mínimo 553,553 y UTM 71,721 porque `Number("553.553")` lee el punto como
 * decimal. En Chile el punto es separador de MILES y la coma el decimal, y así
 * escribe la gente. Este helper interpreta el formato chileno completo:
 *
 *   "553.553"    → 553553        "1.234.567" → 1234567
 *   "41.057,2"   → 41057.2       "41057.2"   → 41057.2
 *   "11,45"      → 11.45         "11.45"     → 11.45
 *   "$ 600.000"  → 600000        ""          → null
 *
 * Regla: si hay punto Y coma, el último de los dos es el decimal. Si solo hay
 * puntos y todos van seguidos de exactamente 3 dígitos ("1.234.567", "553.553",
 * "41.057") son miles; en cualquier otro caso el punto es decimal ("11.45",
 * "0.6"). Si solo hay comas, una es decimal ("11,45") y varias son miles.
 *
 * `parseRate` es para porcentajes/tasas: solo coma o punto decimal, sin miles
 * ("0.006" sigue siendo 0,006 y no 6).
 */

const clean = (v) => String(v ?? '').trim().replace(/[$\s]/g, '');

export const parseAmount = (value) => {
  const s = clean(value);
  if (!s) return null;
  const hasDot = s.includes('.');
  const hasComma = s.includes(',');
  let normalized = s;
  if (hasDot && hasComma) {
    const decimalIsComma = s.lastIndexOf(',') > s.lastIndexOf('.');
    normalized = decimalIsComma ? s.replace(/\./g, '').replace(',', '.') : s.replace(/,/g, '');
  } else if (hasDot) {
    if (/^-?\d{1,3}(\.\d{3})+$/.test(s)) normalized = s.replace(/\./g, '');
  } else if (hasComma) {
    normalized = (s.match(/,/g) || []).length > 1 ? s.replace(/,/g, '') : s.replace(',', '.');
  }
  const n = Number(normalized);
  return Number.isNaN(n) ? null : n;
};

/** Tasas y porcentajes: coma o punto como decimal, nunca miles */
export const parseRate = (value) => {
  const s = clean(value);
  if (!s) return null;
  const n = Number(s.replace(',', '.'));
  return Number.isNaN(n) ? null : n;
};

/** "$ 553.553" (o "41.057,2") para mostrar cómo se entendió lo escrito; '' si no aplica */
export const formatAmount = (value) => {
  if (value === null || value === undefined || value === '') return '';
  const n = typeof value === 'number' ? value : parseAmount(value);
  if (n === null) return '';
  return `$ ${n.toLocaleString('es-CL', { maximumFractionDigits: 2 })}`;
};

/**
 * Pista bajo el campo mientras se escribe: solo cuando el texto trae
 * separadores (ahí es donde había ambigüedad) — "= 553.553". Sin símbolo,
 * porque el mismo campo puede ser pesos, UF o un factor.
 */
export const amountHint = (raw) => {
  const s = clean(raw);
  if (!s || !/[.,]/.test(s)) return '';
  const n = parseAmount(s);
  return n === null ? '' : `= ${n.toLocaleString('es-CL', { maximumFractionDigits: 4 })}`;
};

/**
 * Valores imposibles que delatan un punto de miles leído como decimal.
 * Devuelve el nombre del campo que falla o null.
 */
export const AMOUNT_FLOORS = { minimumWage: 100000, uf: 10000, utm: 10000 };

export const implausibleAmountField = (values) => {
  const found = Object.entries(AMOUNT_FLOORS).find(([field, floor]) => {
    const n = values[field];
    return n !== null && n !== undefined && n !== '' && Number(n) < floor;
  });
  return found ? found[0] : null;
};

export default parseAmount;
